import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Slide,
  makeStyles,
  Card,
  CardContent,
  Chip,
  Link,
  Button,
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Description,
  OpenInNew,
} from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message, Agent, SearchResult } from './types';
import { getAgentIcon, getAgentConfig } from './utils';
import { FilePreview } from './FilePreview';

interface RgUiAction {
  id: string;
  label: string;
  sendText: string;
}

function parseRgUiActions(content: string): { cleanContent: string; actions: RgUiAction[] } {
  const regex = /```rg-ui-actions\n([\s\S]*?)\n```/g;
  let actions: RgUiAction[] = [];
  const cleanContent = content.replace(regex, (_, json) => {
    try {
      actions = JSON.parse(json);
    } catch { /* ignore parse errors */ }
    return '';
  }).trim();
  return { cleanContent, actions };
}

const useStyles = makeStyles(theme => ({
  messageBubbleContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
    alignItems: 'flex-end',
  },
  messageBubbleContainerUser: {
    justifyContent: 'flex-end',
  },
  messageBubbleContainerAgent: {
    justifyContent: 'flex-start',
  },
  avatar: {
    width: 36,
    height: 36,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
    border: `2px solid ${theme.palette.background.paper}`,
  },
  avatarAgent: {
    marginRight: theme.spacing(1.5),
  },
  avatarUser: {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
    marginLeft: theme.spacing(1.5),
  },
  paperUser: {
    padding: theme.spacing(2, 2.5),
    maxWidth: '75%',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    borderRadius: '20px 20px 6px 20px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    position: 'relative',
  },
  paperAgent: {
    padding: theme.spacing(2, 2.5),
    maxWidth: '75%',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: '20px 20px 20px 6px',
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    position: 'relative',
  },
  paperAgentSts: {
    backgroundColor: '#ffffff !important',
    border: '1px solid #e0e0e0 !important',
    borderRadius: '4px 12px 12px 4px !important',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08) !important',
  },
  paperFunction: {
    padding: theme.spacing(1.5, 2),
    maxWidth: '75%',
    backgroundColor: '#f0f7ff',
    color: '#1565c0',
    borderRadius: '20px 20px 20px 6px',
    border: '1px solid #bbdefb',
    boxShadow: '0 2px 12px rgba(21, 101, 192, 0.1)',
    position: 'relative',
  },
  agentLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    display: 'block',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  messageContent: {
    wordBreak: 'break-word',
    lineHeight: 1.5,
    fontFamily:
      'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif, "Helvetica Neue", Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    fontSize: '16px',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: theme.spacing(1, 0, 0.5, 0),
      fontWeight: 600,
      fontFamily:
        'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif',
    },
    '& p': {
      margin: theme.spacing(0.5, 0),
      fontFamily:
        'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif',
      fontSize: '16px',
    },
    '& ul, & ol': {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(2),
      fontFamily:
        'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif',
    },
    '& li': {
      margin: theme.spacing(0.25, 0),
      fontSize: '16px',
      fontFamily:
        'Söhne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif',
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(0.25, 0.5),
      borderRadius: 4,
      fontSize: '14px',
      fontFamily: 'Söhne Mono, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(1),
      borderRadius: 4,
      overflow: 'auto',
      margin: theme.spacing(0.5, 0),
      fontSize: '14px',
      fontFamily: 'Söhne Mono, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: theme.spacing(1, 0),
      fontSize: '14px',
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(0.75, 1.5),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
    },
    '& tr:nth-child(even)': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  timestamp: {
    opacity: 0.6,
    display: 'block',
    textAlign: 'right',
    marginTop: theme.spacing(0.75),
    fontSize: '0.7rem',
    fontWeight: 500,
  },
  functionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  functionDetails: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(21, 101, 192, 0.05)',
    borderRadius: 8,
    border: '1px solid rgba(21, 101, 192, 0.1)',
  },
  expandIcon: {
    padding: 4,
    color: '#1565c0',
  },
  searchResultsContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  searchResultCard: {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 8,
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
  },
  searchResultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  searchResultTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  searchResultScore: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  searchResultMeta: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  searchResultSummary: {
    fontSize: '0.85rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(1),
  },
  searchResultLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.8rem',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  actionButton: {
    textTransform: 'none',
    borderRadius: 24,
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: theme.spacing(0.75, 2.5),
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: '#ffffff',
    border: 'none',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 1px 4px rgba(25, 118, 210, 0.3)',
    },
  },
}));

interface MessageBubbleProps {
  message: Message;
  agents: Agent[];
  stsMode?: boolean;
  onSendMessage?: (text: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  agents,
  stsMode = false,
  onSendMessage,
}) => {
  const classes = useStyles();
  const isUser = message.type === 'user';
  const agentConfig = isUser
    ? null
    : getAgentConfig(message.agentType || 'Assistant', agents);
  const { cleanContent, actions } = isUser
    ? { cleanContent: message.content, actions: [] }
    : parseRgUiActions(message.content);

  return (
    <Slide direction={isUser ? 'left' : 'right'} in timeout={300}>
      <Box
        className={`${classes.messageBubbleContainer} ${
          isUser
            ? classes.messageBubbleContainerUser
            : classes.messageBubbleContainerAgent
        }`}
      >
        {!isUser && agentConfig && (
          <Avatar
            className={`${classes.avatar} ${classes.avatarAgent}`}
            style={{ backgroundColor: agentConfig.color }}
          >
            {getAgentIcon(agentConfig.icon)}
          </Avatar>
        )}
        <Paper
          elevation={2}
          className={`${
            isUser
              ? classes.paperUser
              : message.isFunction
              ? classes.paperFunction
              : classes.paperAgent
          } ${stsMode && !isUser && !message.isFunction ? classes.paperAgentSts : ''}`}
        >
          {!isUser && agentConfig && (
            <Typography
              variant="caption"
              className={classes.agentLabel}
              style={{ color: agentConfig.color }}
            >
              {message.agentType}
            </Typography>
          )}
          {isUser ? (
            <Typography variant="body1" className={classes.messageContent}>
              {message.content}
            </Typography>
          ) : (
            <Box className={classes.messageContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm as any]}>{cleanContent}</ReactMarkdown>
              {actions.length > 0 && (
                <Box className={classes.actionsContainer}>
                  {actions.map(action => (
                    <Button
                      key={action.id}
                      variant="contained"
                      size="small"
                      className={classes.actionButton}
                      onClick={() => onSendMessage?.(action.sendText)}
                      disableElevation
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              )}
              {message.searchResults && message.searchResults.length > 0 && (
                <Box className={classes.searchResultsContainer}>
                  {message.searchResults.map(
                    (result: SearchResult, index: number) => (
                      <Card key={index} className={classes.searchResultCard}>
                        <CardContent>
                          <Box className={classes.searchResultHeader}>
                            <Typography className={classes.searchResultTitle}>
                              <Description fontSize="small" />
                              {result.file_name}
                            </Typography>
                            <Typography
                              className={classes.searchResultScore}
                              style={{
                                color: result.score > 0.5 ? '#4caf50' : '#ff9800',
                              }}
                            >
                              Similarity Score :{' '}
                              {(result.score * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                          <Box className={classes.searchResultMeta}>
                            <Chip label={result.product} size="small" variant="outlined" />
                            <Chip
                              label={`Modified: ${new Date(result.last_modified).toLocaleDateString()}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          <Typography className={classes.searchResultSummary}>
                            {result.summary_20_words}
                          </Typography>
                          <Link
                            href={result.sharepoint_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.searchResultLink}
                          >
                            <OpenInNew fontSize="small" />
                            View Document
                          </Link>
                        </CardContent>
                      </Card>
                    ),
                  )}
                </Box>
              )}
            </Box>
          )}
          {message.files && message.files.length > 0 && (
            <FilePreview files={message.files} />
          )}
          <Typography variant="caption" className={classes.timestamp}>
            {message.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Paper>
        {isUser && (
          <Avatar className={`${classes.avatar} ${classes.avatarUser}`}>
            <PersonIcon />
          </Avatar>
        )}
      </Box>
    </Slide>
  );
};
